import { MAXIMUM_DEPTH_OF_HISTORY } from '@chegg-wt/core/constants/paraphraser'
import type { Step } from '@tiptap/pm/transform'
import { setPaperRequiresRecheck } from '@chegg-wt/core/state/paraphraser/actions'
import { store } from '@chegg-wt/core/state'
import { debounceOnSavePaper } from '@chegg-wt/core/state/paraphraser/utils'
import { Stack } from './stack'
import { FeedbackDelta, HistoryCurrentVersion, HistoryVersion } from './types'
import {
  getHistoryVersionWithStepInJson,
  mergeSteps,
  applyStepsAndGetInversion,
  undoFeedbackDelta,
  redoFeedbackDelta,
} from './utils'

class History {
  private undo: Stack<HistoryVersion>

  private redo: Stack<HistoryVersion>

  private currentVersion: HistoryCurrentVersion

  constructor() {
    this.undo = new Stack()
    this.redo = new Stack()
    this.refreshCurrentVersion()
  }

  private refreshCurrentVersion = () => {
    this.currentVersion = {
      removedGrammarFeedbacks: new Set<String>(),
      removedParaphraseFeedbacks: new Set<String>(),
      addedGrammarFeedbacks: new Set<String>(),
      addedParaphraseFeedbacks: new Set<String>(),
      steps: [],
      feedbackDelta: [],
      sentencesDelta: [],
      changeType: null,
    }
  }

  // Commit the current version data and create a new history version
  public commit = () => {
    // If current version is empty, ignore it
    if (
      this.currentVersion.steps.length === 0 &&
      this.currentVersion.feedbackDelta.length === 0
    ) {
      return
    }
    // This is optimization, we will merge adjacent deltas to have less number of changes
    this.currentVersion.steps = mergeSteps(this.currentVersion.steps.reverse()).reverse()

    if (this.undo.size >= MAXIMUM_DEPTH_OF_HISTORY) {
      // We will clear last versions and only keep track of latest of max depth allowed
      this.undo.removeBottom()
    }
    this.undo.push(getHistoryVersionWithStepInJson(this.currentVersion))

    // Clear next versions on any change
    this.redo.clear()

    // Clear current version to collect new changes
    this.refreshCurrentVersion()
  }

  // pop out from undo stack and apply undo
  public applyUndo = () => {
    const { dispatch, getState } = store
    const state = getState()
    // If we have any steps collected in current version, add that as state
    this.commit()

    if (this.undo.isEmpty()) {
      // Do nothing if user do undo when we have no undo versions
      return null
    }
    const historyVersion = this.undo.pop()

    // Disable review button
    if (historyVersion.enableReviewButton) {
      dispatch(setPaperRequiresRecheck(false))
    }

    // Apply text changes in reverse direction
    const invertedSteps = applyStepsAndGetInversion(
      historyVersion.stepsJson.reverse()
    ).reverse()
    historyVersion.stepsJson = invertedSteps
    // Apply feedback card changes
    undoFeedbackDelta(historyVersion.feedbackDelta, dispatch, state)

    this.redo.push(historyVersion)

    // Save paper
    debounceOnSavePaper(dispatch)
  }

  // pop out from redo stack and apply redo
  public applyRedo = () => {
    const { dispatch, getState } = store
    const state = getState()

    if (this.redo.isEmpty()) {
      // Do nothing if user do redo when we have no redo versions
      return null
    }
    const historyVersion = this.redo.pop()

    // Enable review button
    if (historyVersion.enableReviewButton) {
      dispatch(setPaperRequiresRecheck(true))
    }

    // Apply text changes
    const invertedSteps = applyStepsAndGetInversion(historyVersion.stepsJson)
    historyVersion.stepsJson = invertedSteps
    // Apply feedback card changes
    redoFeedbackDelta(historyVersion.feedbackDelta, dispatch, state)

    this.undo.push(historyVersion)

    // Save paper
    debounceOnSavePaper(dispatch)
  }

  // Clear the history, used on clicking review
  public clear = () => {
    this.undo.clear()
    this.redo.clear()
    this.refreshCurrentVersion()
  }

  public addStep = (step: Step) => {
    this.redo.clear()
    this.currentVersion.steps.push(step)
  }

  public setEnableReviewButton = () => {
    this.currentVersion.enableReviewButton = true
  }

  public addFeedbackChange = (feedbackDelta: FeedbackDelta) => {
    // commit old data so that we can create a separate state for remove/apply card
    this.commit()
    this.currentVersion.feedbackDelta.push(feedbackDelta)
  }
}

const history = new History()
export default history
